import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Fingerprint, X } from "lucide-react";
import {
    isPlatformAuthenticatorAvailable, registerPasskeyForCurrentUser,
    passkeyButtonLabel, isPasskeyFeatureEnabled,
} from "@/lib/webauthn";

/**
 * Non-nagging "Secure your VAULT with a Passkey" banner.
 *
 * Rules:
 *   - Only rendered when the feature flag is ON AND the platform authenticator
 *     is available AND the user has no passkey yet.
 *   - Three actions: Set Up Now / Maybe Later / Don't Ask Again.
 *   - After "Don't Ask Again" the server records `passkey_prompt_state: 'never'`
 *     and this banner never appears again for that user.
 *   - After "Maybe Later" the banner is hidden for the current session only.
 */
export default function PasskeySetupPrompt() {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [featureOn, platformOk, status] = await Promise.all([
                    isPasskeyFeatureEnabled(),
                    isPlatformAuthenticatorAvailable(),
                    api.get("/webauthn/prompt/status").then((r) => r.data).catch(() => null),
                ]);
                if (!featureOn || !platformOk || !status) return;
                if (status.should_prompt) setVisible(true);
            } catch { /* silent */ }
        })();
    }, []);

    const setupNow = async () => {
        setBusy(true);
        try {
            await registerPasskeyForCurrentUser();
            toast.success("Passkey added successfully.");
            setVisible(false);
        } catch (e) {
            const msg = e?.message || "Setup cancelled.";
            if (!/cancel/i.test(msg)) toast.error(msg);
        } finally { setBusy(false); }
    };

    const maybeLater = async () => {
        try { await api.post("/webauthn/prompt/dismiss", { forever: false }); } catch { /* silent */ }
        setVisible(false);
    };

    const dontAsk = async () => {
        try { await api.post("/webauthn/prompt/dismiss", { forever: true }); } catch { /* silent */ }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div className="mx-4 md:mx-6 mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="passkey-setup-prompt">
            <div className="rounded-full bg-blue-100 p-2 shrink-0">
                <Fingerprint size={16} className="text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-stone-900">Secure your VAULT with a Passkey</div>
                <div className="text-xs text-stone-600 mt-0.5">
                    Use Touch ID, Face ID or your device security for faster, safer sign-in.
                    Your fingerprint never leaves your device.
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={setupNow} disabled={busy} data-testid="passkey-setup-now">
                    {busy ? "Setting up…" : passkeyButtonLabel().replace("Sign in with ", "Set Up ")}
                </Button>
                <Button size="sm" variant="ghost" onClick={maybeLater} data-testid="passkey-maybe-later">
                    Maybe later
                </Button>
                <Button size="sm" variant="ghost" onClick={dontAsk} data-testid="passkey-dont-ask">
                    <X size={12} /> Don&apos;t ask again
                </Button>
            </div>
        </div>
    );
}
